import type { ReactNode } from 'react';
import { BarChart3, CalendarDays, ClipboardCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

export const STAFF_ATTENDANCE_TABS = ['punchins', 'report', 'leaves'] as const;
export type StaffAttendanceTabType = (typeof STAFF_ATTENDANCE_TABS)[number];

export interface StaffAttendanceTabsProps {
  activeTab: StaffAttendanceTabType;
  onTabChange: (tab: StaffAttendanceTabType) => void;
  counts?: {
    punchins?: number;
    report?: number;
    leaves?: number;
  };
  rightAction?: ReactNode;
}

export function StaffAttendanceTabs({
  activeTab,
  onTabChange,
  counts,
  rightAction,
}: StaffAttendanceTabsProps) {
  const tabs = [
    {
      id: 'punchins' as const,
      label: 'Punch-ins / Staff Attendance',
      icon: <ClipboardCheck className="h-4 w-4" />,
      count: counts?.punchins,
    },
    {
      id: 'report' as const,
      label: 'Attendance Report',
      icon: <BarChart3 className="h-4 w-4" />,
      count: counts?.report,
    },
    {
      id: 'leaves' as const,
      label: 'Leave Request',
      icon: <CalendarDays className="h-4 w-4" />,
      count: counts?.leaves,
    },
  ];

  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 pb-2 lg:pb-0">
      <div className="flex items-center gap-6 sm:gap-8 overflow-x-auto custom-scrollbar">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={cn(
                'group relative flex items-center gap-2 pb-3 text-[14.5px] font-black transition-colors whitespace-nowrap cursor-pointer focus:outline-none',
                isActive
                  ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-600 dark:border-emerald-400 -mb-px'
                  : 'text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white font-extrabold border-b-2 border-transparent -mb-px',
              )}
            >
              <span
                className={cn(
                  'transition-colors',
                  isActive
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-slate-400 group-hover:text-slate-700 dark:text-slate-500 dark:group-hover:text-slate-200',
                )}
              >
                {tab.icon}
              </span>
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span
                  className={cn(
                    'px-2 py-0.5 rounded-full text-[11px] font-black transition-colors',
                    isActive
                      ? 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {rightAction && (
        <div className="flex items-center shrink-0 self-start lg:self-center pb-1 lg:pb-2">
          {rightAction}
        </div>
      )}
    </div>
  );
}
